import React, { useState, useContext, memo, Fragment, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Accordion,
  useAccordionButton,
  AccordionContext,
} from "react-bootstrap";
import { jwtDecode } from "jwt-decode";
import { LiaFileInvoiceDollarSolid } from "react-icons/lia";
import AttractionsIcon from "@mui/icons-material/Attractions";
import { HiBuildingOffice2 } from "react-icons/hi2";
import { ImLocation2 } from "react-icons/im";
import { LiaNetworkWiredSolid } from "react-icons/lia";
import { GrSystem } from "react-icons/gr";
import { FaWarehouse } from "react-icons/fa";
import * as SettingSelector from "../../../../store/setting/selectors";
import { useSelector } from "react-redux";
import { roleTypes } from "../../../../data/roleType";
import WorkHistoryIcon from "@mui/icons-material/WorkHistory";
import InventoryIcon from "@mui/icons-material/Inventory";
import ShoppingBasketIcon from "@mui/icons-material/ShoppingBasket";

function CustomToggle({ children, eventKey, onClick }) {
  const { activeEventKey } = useContext(AccordionContext);
  const decoratedOnClick = useAccordionButton(eventKey, (active) =>
    onClick({ state: !active, eventKey: eventKey })
  );

  const isCurrentEventKey = activeEventKey === eventKey;

  return (
    <Link
      to="#"
      aria-expanded={isCurrentEventKey ? "true" : "false"}
      className="nav-link"
      role="button"
      onClick={(e) => {
        decoratedOnClick(isCurrentEventKey);
      }}
    >
      {children}
    </Link>
  );
}

const VerticalNav = memo((props) => {
  let location = useLocation();
  const [activeMenu, setActiveMenu] = useState(false);
  const [decodedToken, setDecodedToken] = useState();
  const [activeAccordionKey, setActiveAccordionKey] = useState(null);
  const token = localStorage.getItem("coyote-token");
  const permissions = useSelector(SettingSelector.set_permissions);
  const decodeToken = (token) => {
    try {
      return jwtDecode(token);
    } catch (error) {
      return null;
    }
  };

  useEffect(() => {
    setDecodedToken(decodeToken(token)?.user);
  }, [token]);
  const hasRole = (roleType) => {
    return decodedToken?.roles?.some((role) => role.role_type === roleType);
  };

  const isSuperAdmin = () => hasRole(roleTypes.SUPER_ADMIN);
  const isAdmin = () => hasRole(roleTypes.ADMIN);
  const isPurchasing = () => hasRole(roleTypes.PURCHASING);
  const isSupplier = () => hasRole(roleTypes.SUPPLIER);
  const isWarehouse = () => hasRole(roleTypes.WAREHOUSE);

  const userHasPermission = Object.values(permissions?.user || {}).some(
    Boolean
  );
  const productHasPermission = Object.values(
    permissions?.general_product || {}
  ).some(Boolean);
  const warehouseHasPermission = Object.values(
    permissions?.warehouse || {}
  ).some(Boolean);
  const rackManagementHasPermission = Object.values(
    permissions?.rack_management || {}
  ).some(Boolean);
  const systemHasPermission = Object.values(permissions?.system || {}).some(
    Boolean
  );
  const orderHasPermission = Object.values(
    permissions?.purchase_order || {}
  ).some(Boolean);

  const orderReceiveHasPermission = Object.values(
    permissions?.receiving || {}
  ).some(Boolean);

  const locationHasPermission = Object.values(permissions?.location || {}).some(
    Boolean
  );
  const stakeholderHasPermission = Object.values(
    permissions?.stakeholder || {}
  ).some(Boolean);

  const manufacturerHasPermission = Object.values(
    permissions?.manufacturer || {}
  ).some(Boolean);
  const vendorHasPermission = Object.values(permissions?.vendor || {}).some(
    Boolean
  );
  const phaseHasPermission = Object.values(permissions?.phase || {}).some(
    Boolean
  );

  const jobHasPermission = Object.values(permissions?.job || {}).some(Boolean);
  const productRequestHasPermission = Object.values(
    permissions.product_request || {}
  ).some(Boolean);

  const isProductRelatedPath = () => {
    const productPaths = [
      "/dashboard/app/product-list",
      "/dashboard/app/product-add",
      "/dashboard/app/product-edit",
      "/dashboard/app/category-list",
      "/dashboard/app/category-add",
      "/dashboard/app/category-edit",
    ];
    return productPaths.includes(location.pathname);
  };

  useEffect(() => {
    if (isProductRelatedPath()) {
      setActiveAccordionKey("sidebar-product");
    } else {
      setActiveAccordionKey(null);
    }
  }, [location.pathname]);

  const hasPermission = (
    module,
    actions = ["delete", "view", "update", "create", "status"]
  ) => {
    if (isSuperAdmin()) return true;
    if (isAdmin()) {
      return (
        permissions?.[module] &&
        Object.entries(permissions?.[module])?.some(
          ([key, value]) => actions.includes(key) && value
        )
      );
    }
    return false;
  };

  const canViewList = (module) => {
    if (isSuperAdmin()) return true;
    if (isAdmin()) {
      return (
        permissions?.[module] &&
        (permissions[module].view ||
          permissions[module].update ||
          permissions[module].delete ||
          permissions[module].status)
      );
    }
    return false;
  };

  const getDefaultRoute = (module) => {
    if (isSuperAdmin()) {
      if (module === "general_product_category") {
        return `category-list`;
      } else if (module === "general_product") {
        return `product-list`;
      }
    }
    if (isAdmin() && canViewList(module)) {
      if (module === "general_product_category") {
        return `category-list`;
      } else if (module === "general_product") {
        return `product-list`;
      }
    }
    if (module === "general_product_category") {
      return `category-add`;
    } else if (module === "general_product") {
      return `product-add`;
    }
  };

  return (
    <Fragment>
      <Accordion
        as="ul"
        className="navbar-nav iq-main-menu mb-5"
        activeKey={activeAccordionKey}
        onSelect={(key) => setActiveAccordionKey(key)}
      >
        <li className="nav-item static-item">
          <Link className="nav-link static-item disabled" to="#" tabIndex="-1">
            <span className="default-icon">Home</span>
            <span className="mini-icon">-</span>
          </Link>
        </li>
        <li
          className={`${
            location.pathname === "/dashboard" ? "active" : ""
          } nav-item `}
        >
          <Link
            className={`${
              location.pathname === "/dashboard" ? "active" : ""
            } nav-link `}
            aria-current="page"
            to="/dashboard"
            onClick={() => {}}
          >
            <i className="icon">
              <svg
                width="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  opacity="0.4"
                  d="M16.0756 2H19.4616C20.8639 2 22.0001 3.14585 22.0001 4.55996V7.97452C22.0001 9.38864 20.8639 10.5345 19.4616 10.5345H16.0756C14.6734 10.5345 13.5371 9.38864 13.5371 7.97452V4.55996C13.5371 3.14585 14.6734 2 16.0756 2Z"
                  fill="currentColor"
                ></path>
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M4.53852 2H7.92449C9.32676 2 10.463 3.14585 10.463 4.55996V7.97452C10.463 9.38864 9.32676 10.5345 7.92449 10.5345H4.53852C3.13626 10.5345 2 9.38864 2 7.97452V4.55996C2 3.14585 3.13626 2 4.53852 2ZM4.53852 13.4655H7.92449C9.32676 13.4655 10.463 14.6114 10.463 16.0255V19.44C10.463 20.8532 9.32676 22 7.92449 22H4.53852C3.13626 22 2 20.8532 2 19.44V16.0255C2 14.6114 3.13626 13.4655 4.53852 13.4655ZM19.4615 13.4655H16.0755C14.6732 13.4655 13.537 14.6114 13.537 16.0255V19.44C13.537 20.8532 14.6732 22 16.0755 22H19.4615C20.8637 22 22 20.8532 22 19.44V16.0255C22 14.6114 20.8637 13.4655 19.4615 13.4655Z"
                  fill="currentColor"
                ></path>
              </svg>
            </i>
            <span className="item-name">Dashboard</span>
          </Link>
        </li>

        <li>
          <hr className="hr-horizontal" />
        </li>
        <li className="nav-item static-item">
          <Link className="nav-link static-item disabled" to="#" tabIndex="-1">
            <span className="default-icon">Pages</span>
            <span className="mini-icon">-</span>
          </Link>
        </li>

        {(isSuperAdmin() || (isAdmin() && userHasPermission)) && (
          <li
            className={`${
              location.pathname === "/dashboard/app/user-list" ||
              location.pathname === "/dashboard/app/user-add" ||
              location.pathname === "/dashboard/app/user-edit"
                ? "active"
                : ""
            } nav-item `}
          >
            <Link
              className={`${
                location.pathname === "/dashboard/app/user-list" ||
                location.pathname === "/dashboard/app/user-add" ||
                location.pathname === "/dashboard/app/user-edit"
                  ? "active"
                  : ""
              } nav-link `}
              aria-current="page"
              to={`/dashboard/app/${
                isSuperAdmin() ||
                (isAdmin() &&
                  permissions?.user &&
                  Object.entries(permissions?.user).some(
                    ([key, value]) =>
                      ["delete", "view", "update", "status"].includes(key) &&
                      value
                  ))
                  ? "user-list"
                  : "user-add"
              }`}
              onClick={() => {}}
            >
              <i className="icon">
                <svg
                  width="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M11.9488 14.54C8.49884 14.54 5.58789 15.1038 5.58789 17.2795C5.58789 19.4562 8.51765 20.0001 11.9488 20.0001C15.3988 20.0001 18.3098 19.4364 18.3098 17.2606C18.3098 15.084 15.38 14.54 11.9488 14.54Z"
                    fill="currentColor"
                  ></path>
                  <path
                    opacity="0.4"
                    d="M11.949 12.467C14.2851 12.467 16.1583 10.5831 16.1583 8.23351C16.1583 5.88306 14.2851 4 11.949 4C9.61293 4 7.73975 5.88306 7.73975 8.23351C7.73975 10.5831 9.61293 12.467 11.949 12.467Z"
                    fill="currentColor"
                  ></path>
                  <path
                    opacity="0.4"
                    d="M21.0881 9.21923C21.6925 6.84176 19.9205 4.70654 17.664 4.70654C17.4187 4.70654 17.1841 4.73356 16.9549 4.77949C16.9244 4.78669 16.8904 4.802 16.8725 4.82902C16.8519 4.86324 16.8671 4.90917 16.8895 4.93889C17.5673 5.89528 17.9568 7.0597 17.9568 8.30967C17.9568 9.50741 17.5996 10.6241 16.9728 11.5508C16.9083 11.6462 16.9656 11.775 17.0793 11.7948C17.2369 11.8227 17.3981 11.8371 17.5629 11.8416C19.2059 11.8849 20.6807 10.8213 21.0881 9.21923Z"
                    fill="currentColor"
                  ></path>
                  <path
                    d="M22.8094 14.817C22.5086 14.1722 21.7824 13.73 20.6783 13.513C20.1572 13.3851 18.747 13.205 17.4352 13.2293C17.4155 13.232 17.4048 13.2455 17.403 13.2545C17.4003 13.2671 17.4057 13.2887 17.4316 13.3022C18.0378 13.6039 20.3811 14.916 20.0865 17.6834C20.074 17.8032 20.1698 17.9068 20.2888 17.8888C20.8655 17.8059 22.3492 17.4853 22.8094 16.4866C23.0637 15.9589 23.0637 15.3456 22.8094 14.817Z"
                    fill="currentColor"
                  ></path>
                  <path
                    opacity="0.4"
                    d="M7.04459 4.77973C6.81626 4.7329 6.58077 4.70679 6.33543 4.70679C4.07901 4.70679 2.30701 6.84201 2.9123 9.21947C3.31882 10.8216 4.79355 11.8851 6.43661 11.8419C6.60136 11.8374 6.76343 11.8221 6.92013 11.7951C7.03384 11.7753 7.09115 11.6465 7.02668 11.551C6.3999 10.6234 6.04263 9.50765 6.04263 8.30991C6.04263 7.05904 6.43303 5.89462 7.11085 4.93913C7.13234 4.90941 7.14845 4.86348 7.12696 4.82926C7.10906 4.80135 7.07593 4.78694 7.04459 4.77973Z"
                    fill="currentColor"
                  ></path>
                  <path
                    d="M3.32156 13.5127C2.21752 13.7297 1.49225 14.1719 1.19139 14.8167C0.936203 15.3453 0.936203 15.9586 1.19139 16.4872C1.65163 17.4851 3.13531 17.8066 3.71195 17.8885C3.83104 17.9065 3.92595 17.8038 3.91342 17.6832C3.61883 14.9167 5.9621 13.6046 6.56918 13.3029C6.59425 13.2885 6.59962 13.2677 6.59694 13.2542C6.59515 13.2452 6.5853 13.2317 6.5656 13.2299C5.25294 13.2047 3.84358 13.3848 3.32156 13.5127Z"
                    fill="currentColor"
                  ></path>
                </svg>
              </i>
              <span className="item-name">Users</span>
            </Link>
          </li>
        )}

        {(isAdmin() || isWarehouse()) && userHasPermission && !isAdmin() && (
          <li
            className={`${
              location.pathname === "/dashboard/app/warehouse/user-list" ||
              location.pathname === "/dashboard/app/warehouse/user-add" ||
              location.pathname === "/dashboard/app/warehouse/user-edit"
                ? "active"
                : ""
            } nav-item `}
          >
            <Link
              className={`${
                location.pathname === "/dashboard/app/warehouse/user-list" ||
                location.pathname === "/dashboard/app/warehouse/user-add" ||
                location.pathname === "/dashboard/app/warehouse/user-edit"
                  ? "active"
                  : ""
              } nav-link `}
              aria-current="page"
              to={`/dashboard/app/warehouse/${
                isWarehouse() &&
                permissions?.user &&
                Object.entries(permissions?.user).some(
                  ([key, value]) =>
                    ["status", "delete", "view", "update"].includes(key) &&
                    value
                )
                  ? "user-list"
                  : isWarehouse() && "user-add"
              }`}
              onClick={() => {}}
            >
              <i className="icon">
                <svg
                  width="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M11.9488 14.54C8.49884 14.54 5.58789 15.1038 5.58789 17.2795C5.58789 19.4562 8.51765 20.0001 11.9488 20.0001C15.3988 20.0001 18.3098 19.4364 18.3098 17.2606C18.3098 15.084 15.38 14.54 11.9488 14.54Z"
                    fill="currentColor"
                  ></path>
                  <path
                    opacity="0.4"
                    d="M11.949 12.467C14.2851 12.467 16.1583 10.5831 16.1583 8.23351C16.1583 5.88306 14.2851 4 11.949 4C9.61293 4 7.73975 5.88306 7.73975 8.23351C7.73975 10.5831 9.61293 12.467 11.949 12.467Z"
                    fill="currentColor"
                  ></path>
                  <path
                    opacity="0.4"
                    d="M21.0881 9.21923C21.6925 6.84176 19.9205 4.70654 17.664 4.70654C17.4187 4.70654 17.1841 4.73356 16.9549 4.77949C16.9244 4.78669 16.8904 4.802 16.8725 4.82902C16.8519 4.86324 16.8671 4.90917 16.8895 4.93889C17.5673 5.89528 17.9568 7.0597 17.9568 8.30967C17.9568 9.50741 17.5996 10.6241 16.9728 11.5508C16.9083 11.6462 16.9656 11.775 17.0793 11.7948C17.2369 11.8227 17.3981 11.8371 17.5629 11.8416C19.2059 11.8849 20.6807 10.8213 21.0881 9.21923Z"
                    fill="currentColor"
                  ></path>
                  <path
                    d="M22.8094 14.817C22.5086 14.1722 21.7824 13.73 20.6783 13.513C20.1572 13.3851 18.747 13.205 17.4352 13.2293C17.4155 13.232 17.4048 13.2455 17.403 13.2545C17.4003 13.2671 17.4057 13.2887 17.4316 13.3022C18.0378 13.6039 20.3811 14.916 20.0865 17.6834C20.074 17.8032 20.1698 17.9068 20.2888 17.8888C20.8655 17.8059 22.3492 17.4853 22.8094 16.4866C23.0637 15.9589 23.0637 15.3456 22.8094 14.817Z"
                    fill="currentColor"
                  ></path>
                  <path
                    opacity="0.4"
                    d="M7.04459 4.77973C6.81626 4.7329 6.58077 4.70679 6.33543 4.70679C4.07901 4.70679 2.30701 6.84201 2.9123 9.21947C3.31882 10.8216 4.79355 11.8851 6.43661 11.8419C6.60136 11.8374 6.76343 11.8221 6.92013 11.7951C7.03384 11.7753 7.09115 11.6465 7.02668 11.551C6.3999 10.6234 6.04263 9.50765 6.04263 8.30991C6.04263 7.05904 6.43303 5.89462 7.11085 4.93913C7.13234 4.90941 7.14845 4.86348 7.12696 4.82926C7.10906 4.80135 7.07593 4.78694 7.04459 4.77973Z"
                    fill="currentColor"
                  ></path>
                  <path
                    d="M3.32156 13.5127C2.21752 13.7297 1.49225 14.1719 1.19139 14.8167C0.936203 15.3453 0.936203 15.9586 1.19139 16.4872C1.65163 17.4851 3.13531 17.8066 3.71195 17.8885C3.83104 17.9065 3.92595 17.8038 3.91342 17.6832C3.61883 14.9167 5.9621 13.6046 6.56918 13.3029C6.59425 13.2885 6.59962 13.2677 6.59694 13.2542C6.59515 13.2452 6.5853 13.2317 6.5656 13.2299C5.25294 13.2047 3.84358 13.3848 3.32156 13.5127Z"
                    fill="currentColor"
                  ></path>
                </svg>
              </i>
              <span className="item-name">Users</span>
            </Link>
          </li>
        )}
        {isSuperAdmin() && (
          <li
            className={`${
              location.pathname === "/dashboard/app/role-list" ? "active" : ""
            } nav-item `}
          >
            <Link
              className={`${
                location.pathname === "/dashboard/app/role-list" ? "active" : ""
              } nav-link `}
              aria-current="page"
              to="/dashboard/app/role-list"
              onClick={() => {}}
            >
              <i className="icon">
                <svg
                  width="20"
                  xmlns="http://www.w3.org/2000/svg"
                  x="0px"
                  y="0px"
                  fill="none"
                  viewBox="0 0 48 48"
                >
                  <path
                    d="M 12 3 C 10.125 3 8.5288151 3.7571334 7.5039062 4.9101562 C 6.4789975 6.0631791 6 7.5416668 6 9 C 6 10.458333 6.4789975 11.936821 7.5039062 13.089844 C 8.528815 14.242867 10.125 15 12 15 C 13.875 15 15.471185 14.242867 16.496094 13.089844 C 17.521003 11.936821 18 10.458333 18 9 C 18 7.5416668 17.521003 6.0631791 16.496094 4.9101562 C 15.471185 3.7571334 13.875 3 12 3 z M 26 3 C 24.125 3 22.528815 3.7571334 21.503906 4.9101562 C 20.478997 6.0631791 20 7.5416668 20 9 C 20 10.458333 20.478997 11.936821 21.503906 13.089844 C 22.528815 14.242867 24.125 15 26 15 C 27.875 15 29.471185 14.242867 30.496094 13.089844 C 31.521003 11.936821 32 10.458333 32 9 C 32 7.5416668 31.521003 6.0631791 30.496094 4.9101562 C 29.471185 3.7571334 27.875 3 26 3 z M 12 6 C 13.124999 6 13.778816 6.3678666 14.253906 6.9023438 C 14.728997 7.4368209 15 8.2083332 15 9 C 15 9.7916668 14.728997 10.563179 14.253906 11.097656 C 13.778816 11.632133 13.124999 12 12 12 C 10.875001 12 10.221184 11.632133 9.7460938 11.097656 C 9.2710032 10.563179 9 9.7916668 9 9 C 9 8.2083332 9.2710032 7.4368209 9.7460938 6.9023438 C 10.221184 6.3678666 10.875001 6 12 6 z M 26 6 C 27.124999 6 27.778816 6.3678666 28.253906 6.9023438 C 28.728997 7.4368209 29 8.2083332 29 9 C 29 9.7916668 28.728997 10.563179 28.253906 11.097656 C 27.778816 11.632133 27.124999 12 26 12 C 24.875001 12 24.221184 11.632133 23.746094 11.097656 C 23.271003 10.563179 23 9.7916668 23 9 C 23 8.2083332 23.271003 7.4368209 23.746094 6.9023438 C 24.221184 6.3678666 24.875001 6 26 6 z M 6.5 17 C 4.57 17 3 18.57 3 20.5 L 3 30 C 3 34.963 7.038 39 12 39 C 14.104 39 16.036313 38.268688 17.570312 37.054688 C 16.923313 36.282688 16.38675 35.418281 15.96875 34.488281 C 14.90975 35.425281 13.522 36 12 36 C 8.691 36 6 33.309 6 30 L 6 20.5 C 6 20.225 6.224 20 6.5 20 L 15.025391 20 C 15.128391 18.867 15.573719 17.831 16.261719 17 L 6.5 17 z M 20.5 17 C 18.57 17 17 18.57 17 20.5 L 17 30 C 17 33.694 19.237734 36.871813 22.427734 38.257812 C 22.157734 37.214813 22 36.127 22 35 C 22 34.826 22.018391 34.656375 22.025391 34.484375 C 20.785391 33.384375 20 31.784 20 30 L 20 20.5 C 20 20.225 20.224 20 20.5 20 L 31.5 20 C 31.776 20 32 20.225 32 20.5 L 32 22.361328 C 32.463 22.251328 32.933062 22.165422 33.414062 22.107422 C 33.441063 22.104422 33.465187 22.098703 33.492188 22.095703 C 33.987187 22.037703 34.489 22 35 22 L 35 20.5 C 35 18.57 33.43 17 31.5 17 L 20.5 17 z M 35 24 C 34.413 24 33.838344 24.049531 33.277344 24.144531 L 32.931641 25.576172 C 32.394641 27.801172 30.105156 29.123609 27.910156 28.474609 L 26.705078 28.117188 C 25.981078 29.050187 25.401 30.110813 25 31.257812 L 25.894531 32.117188 C 27.529531 33.691187 27.529531 36.308813 25.894531 37.882812 L 25 38.742188 C 25.401 39.889187 25.980078 40.948812 26.705078 41.882812 L 27.910156 41.525391 C 30.105156 40.876391 32.394641 42.198828 32.931641 44.423828 L 33.277344 45.855469 C 33.838344 45.950469 34.414 46 35 46 C 35.587 46 36.161656 45.950469 36.722656 45.855469 L 37.068359 44.423828 C 37.605359 42.198828 39.894844 40.876391 42.089844 41.525391 L 43.294922 41.882812 C 44.018922 40.949813 44.599 39.889187 45 38.742188 L 44.105469 37.882812 C 42.470469 36.309812 42.470469 33.690188 44.105469 32.117188 L 45 31.257812 C 44.599 30.110813 44.019922 29.051187 43.294922 28.117188 L 42.089844 28.474609 C 39.894844 29.123609 37.605359 27.801172 37.068359 25.576172 L 36.722656 24.144531 C 36.161656 24.049531 35.587 24 35 24 z M 35 32 C 36.657 32 38 33.343 38 35 C 38 36.657 36.657 38 35 38 C 33.343 38 32 36.657 32 35 C 32 33.343 33.343 32 35 32 z"
                    fill="currentColor"
                  ></path>
                </svg>
              </i>
              <span className="item-name">Roles</span>
            </Link>
          </li>
        )}

        {(isSuperAdmin() ||
          ((isAdmin() || isWarehouse()) && stakeholderHasPermission)) && (
          <li
            className={`${
              location.pathname === "/dashboard/app/stakeholder-list" ||
              location.pathname === "/dashboard/app/stakeholder-add" ||
              location.pathname === "/dashboard/app/stakeholder-edit"
                ? "active"
                : ""
            } nav-item `}
          >
            <Link
              className={`${
                location.pathname === "/dashboard/app/stakeholder-list" ||
                location.pathname === "/dashboard/app/stakeholder-add" ||
                location.pathname === "/dashboard/app/stakeholder-edit"
                  ? "active"
                  : ""
              } nav-link `}
              aria-current="page"
              to={`/dashboard/app/${
                isSuperAdmin() ||
                (permissions?.stakeholder &&
                  Object.entries(permissions.stakeholder).some(
                    ([key, value]) =>
                      [
                        "status",
                        "delete",
                        "view",
                        "update",
                        "delete_contact",
                      ].includes(key) && value
                  ))
                  ? "stakeholder-list"
                  : "stakeholder-add"
              }`}
              onClick={() => {}}
            >
              <i className="icon">
                <HiBuildingOffice2 style={{ width: "20px" }} />
              </i>
              <span className="item-name">Stakeholders</span>
            </Link>
          </li>
        )}
        {(isSuperAdmin() ||
          ((isAdmin() || isWarehouse()) && locationHasPermission)) && (
          <li
            className={`${
              location.pathname === "/dashboard/app/location-list" ||
              location.pathname === "/dashboard/app/location-add" ||
              location.pathname === "/dashboard/app/location-edit"
                ? "active"
                : ""
            } nav-item `}
          >
            <Link
              className={`${
                location.pathname === "/dashboard/app/location-list" ||
                location.pathname === "/dashboard/app/location-add" ||
                location.pathname === "/dashboard/app/location-edit"
                  ? "active"
                  : ""
              } nav-link `}
              aria-current="page"
              to={`/dashboard/app/${
                isSuperAdmin() ||
                (permissions?.location &&
                  Object.entries(permissions.location).some(
                    ([key, value]) =>
                      ["status", "delete", "view", "update"].includes(key) &&
                      value
                  ))
                  ? "location-list"
                  : "location-add"
              }`}
              onClick={() => {}}
            >
              <i className="icon">
                <ImLocation2 style={{ width: "20px" }} />
              </i>
              <span className="item-name">Locations</span>
            </Link>
          </li>
        )}

        {(isSuperAdmin() ||
          ((isAdmin() || isWarehouse()) && warehouseHasPermission)) && (
          <li
            className={`${
              location.pathname === "/dashboard/app/warehouse-list" ||
              location.pathname === "/dashboard/app/warehouse-add" ||
              location.pathname === "/dashboard/app/warehouse-edit"
                ? "active"
                : ""
            } nav-item `}
          >
            <Link
              className={`${
                location.pathname === "/dashboard/app/warehouse-list" ||
                location.pathname === "/dashboard/app/warehouse-add" ||
                location.pathname === "/dashboard/app/warehouse-edit"
                  ? "active"
                  : ""
              } nav-link `}
              aria-current="page"
              to={`/dashboard/app/${
                isSuperAdmin() ||
                (permissions?.warehouse &&
                  Object.entries(permissions?.warehouse).some(
                    ([key, value]) =>
                      [
                        "view",
                        "update",
                        "status",
                        "view_product_bin",
                        "fill_product_into_bin",
                        "view_warehouse_product",
                        "import_product",
                      ].includes(key) && value
                  ))
                  ? "warehouse-list"
                  : isSuperAdmin() && "warehouse-add"
              }`}
              onClick={() => {}}
            >
              <i className="icon">
                <FaWarehouse width="20px" />
              </i>
              <span className="item-name">Warehouses</span>
            </Link>
          </li>
        )}
        {/* {isSuperAdmin() && (
          <li
            className={`${
              location.pathname === "/dashboard/app/quote" ? "active" : ""
            } nav-item `}
          >
            <Link
              className={`${
                location.pathname === "/dashboard/app/quote" ? "active" : ""
              } nav-link `}
              aria-current="page"
              to="/dashboard/app/quote"
              onClick={() => {}}
              style={{
                display: "flex",
                alignItems: "center",
                width: "-webkit-fill-available",
              }}
            >
              <i className="icon">
                <LiaFileInvoiceDollarSolid style={{ width: "20px" }} />
              </i>
              <span className="item-name">Quote</span>
            </Link>
          </li>
        )} */}

        {(hasPermission("general_product") ||
          hasPermission("general_product_category")) && (
          <Accordion.Item
            as="li"
            eventKey="sidebar-product"
            bsPrefix={`nav-item ${isProductRelatedPath() ? "active" : ""} `}
          >
            <CustomToggle
              eventKey="sidebar-product"
              onClick={(activeKey) => setActiveMenu(activeKey)}
            >
              <Link
                className=""
                style={{
                  padding: "0",
                  display: "flex",
                  alignItems: "center",
                  width: "-webkit-fill-available",
                  color: "unset",
                }}
                to={`/dashboard/app/${
                  hasPermission("general_product")
                    ? getDefaultRoute("general_product")
                    : getDefaultRoute("general_product_category")
                }`}
              >
                <i className="icon">
                  <svg
                    width="20"
                    xmlns="http://www.w3.org/2000/svg"
                    shapeRendering="geometricPrecision"
                    textRendering="geometricPrecision"
                    imageRendering="optimizeQuality"
                    fillRule="evenodd"
                    clipRule="evenodd"
                    fill="none"
                    viewBox="0 0 505 512.36"
                  >
                    <path
                      fill="currentColor"
                      d="m336.11 39.84-115.38 68.95 135.38 18.39 111.32-69.44-131.32-17.9zm26.45 204.72c73.95 0 133.9 59.95 133.9 133.9s-59.95 133.9-133.9 133.9-133.9-59.95-133.9-133.9 59.95-133.9 133.9-133.9zm-21.33 84.62c-.05-6.29-.63-10.78 7.18-10.67l25.27.3c8.16-.05 10.33 2.54 10.22 10.18v28.52h28.31c6.3-.05 10.79-.63 10.68 7.18l-.31 25.26c.04 8.16-2.54 10.32-10.18 10.23h-28.5v28.49c.11 7.65-2.06 10.23-10.22 10.18l-25.27.3c-7.81.13-7.23-4.37-7.18-10.66v-28.31h-28.52c-7.63.09-10.23-2.07-10.18-10.23l-.3-25.26c-.11-7.81 4.38-7.23 10.67-7.18h28.33v-28.33zm-136.4-203.05-.09 141.71-51.46-35.04-51.45 29.07 6.1-148.91-88.54-12.03v312.98l178.95 23.14c2.52 7.09 5.47 13.98 8.85 20.62L9.29 432.08C4.13 431.87 0 427.6 0 422.39V89.86c.27-4.05 1.89-6.89 5.72-8.81L182.47.85c1.58-.72 3.53-1.01 5.26-.76l308.18 42.03c5.09.59 8.58 4.77 8.58 9.99v.02L505 280.9c-5.72-8.46-15.57-20.28-19.93-27.77V69.56l-115.81 74.93v59.81a174.777 174.777 0 0 0-19.39.36v-58.82l-145.04-19.71zm-81.52-30.58 112.17-69.43-47.58-6.49L44.24 84.8l79.07 10.75z"
                    />
                  </svg>
                </i>
                <span className="item-name">
                  {hasPermission("general_product")
                    ? "General Products"
                    : "Categories"}
                </span>
                <i className="right-icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </i>
              </Link>
            </CustomToggle>

            <Accordion.Collapse eventKey="sidebar-product">
              <ul className="sub-nav">
                {hasPermission("general_product") && (
                  <li className="nav-item">
                    <Link
                      className={`${
                        [
                          "/dashboard/app/product-list",
                          "/dashboard/app/product-add",
                          "/dashboard/app/product-edit",
                        ].includes(location.pathname)
                          ? "active"
                          : ""
                      } nav-link`}
                      to={`/dashboard/app/${getDefaultRoute(
                        "general_product"
                      )}`}
                    >
                      <i className="icon">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="10"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                        >
                          <g>
                            <circle
                              cx="12"
                              cy="12"
                              r="8"
                              fill="currentColor"
                            ></circle>
                          </g>
                        </svg>
                      </i>
                      <i className="sidenav-mini-icon"> GP </i>
                      <span className="item-name">General Products</span>
                    </Link>
                  </li>
                )}

                {hasPermission("general_product_category") && (
                  <li className="nav-item">
                    <Link
                      className={`${
                        [
                          "/dashboard/app/category-list",
                          "/dashboard/app/category-add",
                          "/dashboard/app/category-edit",
                        ].includes(location.pathname)
                          ? "active"
                          : ""
                      } nav-link`}
                      to={`/dashboard/app/${getDefaultRoute(
                        "general_product_category"
                      )}`}
                    >
                      <i className="icon">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="10"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                        >
                          <g>
                            <circle
                              cx="12"
                              cy="12"
                              r="8"
                              fill="currentColor"
                            ></circle>
                          </g>
                        </svg>
                      </i>
                      <i className="sidenav-mini-icon"> C </i>
                      <span className="item-name">Categories</span>
                    </Link>
                  </li>
                )}
              </ul>
            </Accordion.Collapse>
          </Accordion.Item>
        )}

        {(isSuperAdmin() || (isAdmin() && manufacturerHasPermission)) && (
          <li
            className={`${
              location.pathname === "/dashboard/app/manufacturer-list" ||
              location.pathname === "/dashboard/app/manufacturer-add" ||
              location.pathname === "/dashboard/app/manufacturer-edit"
                ? "active"
                : ""
            } nav-item `}
          >
            <Link
              className={`${
                location.pathname === "/dashboard/app/manufacturer-list" ||
                location.pathname === "/dashboard/app/manufacturer-add" ||
                location.pathname === "/dashboard/app/manufacturer-edit"
                  ? "active"
                  : ""
              } nav-link `}
              aria-current="page"
              to={`/dashboard/app/${
                isSuperAdmin() ||
                (permissions?.manufacturer &&
                  Object.entries(permissions.manufacturer).some(
                    ([key, value]) =>
                      ["status", "delete", "view", "update"].includes(key) &&
                      value
                  ))
                  ? "manufacturer-list"
                  : "manufacturer-add"
              }`}
              onClick={() => {}}
            >
              <i className="icon">
                <LiaNetworkWiredSolid style={{ width: "20px" }} />
              </i>
              <span className="item-name">Manufacturers</span>
            </Link>
          </li>
        )}

        {(isSuperAdmin() ||
          ((isAdmin() || isWarehouse()) && vendorHasPermission)) && (
          <li
            className={`${
              location.pathname === "/dashboard/app/vendor-list" ||
              location.pathname === "/dashboard/app/vendor-add" ||
              location.pathname === "/dashboard/app/vendor-edit"
                ? "active"
                : ""
            } nav-item `}
          >
            <Link
              className={`${
                location.pathname === "/dashboard/app/vendor-list" ||
                location.pathname === "/dashboard/app/vendor-add" ||
                location.pathname === "/dashboard/app/vendor-edit"
                  ? "active"
                  : ""
              } nav-link `}
              aria-current="page"
              to={`/dashboard/app/${
                isSuperAdmin() ||
                (permissions?.vendor &&
                  Object.entries(permissions.vendor).some(
                    ([key, value]) =>
                      ["status", "delete", "view", "update"].includes(key) &&
                      value
                  ))
                  ? "vendor-list"
                  : "vendor-add"
              }`}
              onClick={() => {}}
            >
              <i className="icon">
                <svg
                  width="20"
                  xmlns="http://www.w3.org/2000/svg"
                  shapeRendering="geometricPrecision"
                  textRendering="geometricPrecision"
                  imageRendering="optimizeQuality"
                  fill="none"
                  viewBox="0 0 512 330.845"
                >
                  <path
                    fill="currentColor"
                    d="M95.992 0c35.923 22.768 68.373 33.54 96.223 30.995 4.865 98.382-31.466 156.48-95.852 180.727C34.188 189.028-2.587 133.427.142 29.502 32.834 31.213 64.91 24.144 95.992 0zM76.731 103.923a73.156 73.156 0 016.88 6.658c6.723-10.822 13.89-20.757 21.461-29.895 21.401-25.849 11.702-20.867 41.389-20.867l-4.124 4.581c-12.676 14.086-16.952 21.417-27.343 36.429a425.653 425.653 0 00-27.95 46.499l-2.571 4.96-2.363-5.052c-4.359-9.359-9.581-17.95-15.808-25.625-6.228-7.676-11.667-12.684-20.112-18.479 3.87-12.702 22.288-6.201 30.541.791zm301.485 1.74l-35.138-.243V57.97a25.356 25.356 0 00-7.529-18.079 25.353 25.353 0 00-18.079-7.53H213.806c-.435 4.496-1.122 9.147-1.833 13.884H317.47c3.218 0 6.159 1.334 8.278 3.449 2.116 2.12 3.45 5.061 3.45 8.276v217.288H290.06a6.93 6.93 0 00-6.94 6.944 6.925 6.925 0 006.94 6.938h46.077a6.926 6.926 0 006.941-6.938v-7.885h28.044c3.177-72.232 106.9-82.195 117.451 0h22.782c5.868-70.433-28.909-97.805-81.803-103.996-3.805-16.53-11.062-31.874-19.26-47.037-9.747-18.025-12.016-17.297-32.076-17.621zM147.08 275.222a6.929 6.929 0 016.944 6.939 6.93 6.93 0 01-6.944 6.941H73.343c-7.022 0-13.413-3.06-18.082-7.882-4.623-4.821-7.527-11.411-7.527-18.392v-48.35a138.893 138.893 0 0013.881 7.815v40.535c0 3.334 1.375 6.51 3.609 8.824 2.119 2.197 4.98 3.606 8.077 3.606h73.779v-.036zm70.59-38.416c-25.963 0-47.019 21.059-47.019 47.019 0 25.961 21.056 47.02 47.019 47.02 25.961 0 47.017-21.059 47.017-47.02-.038-25.96-21.056-47.019-47.017-47.019zm0 28.942c-9.96 0-18.08 8.08-18.08 18.077 0 9.961 8.08 18.082 18.08 18.082 9.959 0 18.079-8.081 18.079-18.082-.042-9.997-8.12-18.077-18.079-18.077zm212.039-35.86c-25.959 0-47.016 21.059-47.016 47.018 0 25.96 21.057 47.021 47.016 47.021 25.963 0 47.02-21.061 47.02-47.021 0-25.959-21.057-47.018-47.02-47.018zm-18.077 47.018c0 9.961 8.076 18.079 18.077 18.079 10.001 0 18.079-8.077 18.079-18.079 0-9.999-8.078-18.076-18.079-18.076-9.978 0-18.077 8.095-18.077 18.076zm-30.038-151.174l-21.182-.392v45.06h44.866c-5.534-16.073-13.724-30.807-23.684-44.668zM96.049 14.47c30.429 19.287 59.636 30.128 83.227 27.971 4.118 83.335-28.373 134.27-82.908 154.808-52.671-19.224-85.542-68.035-83.23-156.073C43.7 42.778 71.71 33.379 96.049 14.47z"
                  />
                </svg>
              </i>
              <span className="item-name">Vendors</span>
            </Link>
          </li>
        )}

        {(isSuperAdmin() ||
          ((isAdmin() || isWarehouse()) && phaseHasPermission)) && (
          <li
            className={`${
              location.pathname === "/dashboard/app/phase-list" ||
              location.pathname === "/dashboard/app/phase-add" ||
              location.pathname === "/dashboard/app/phase-edit"
                ? "active"
                : ""
            } nav-item `}
          >
            <Link
              className={`${
                location.pathname === "/dashboard/app/phase-list" ||
                location.pathname === "/dashboard/app/phase-add" ||
                location.pathname === "/dashboard/app/phase-edit"
                  ? "active"
                  : ""
              } nav-link `}
              aria-current="page"
              to={`/dashboard/app/${
                isSuperAdmin() ||
                (permissions?.phase &&
                  Object.entries(permissions.phase).some(
                    ([key, value]) =>
                      ["status", "delete", "view", "update"].includes(key) &&
                      value
                  ))
                  ? "phase-list"
                  : "phase-add"
              }`}
              onClick={() => {}}
            >
              <i className="icon">
                <AttractionsIcon style={{ width: "20px" }} />
              </i>
              <span className="item-name">Phases</span>
            </Link>
          </li>
        )}
        {(isSuperAdmin() ||
          ((isAdmin() || isWarehouse()) && systemHasPermission)) && (
          <li
            className={`${
              location.pathname === "/dashboard/app/system-list" ||
              location.pathname === "/dashboard/app/system-add" ||
              location.pathname === "/dashboard/app/system-edit"
                ? "active"
                : ""
            } nav-item `}
          >
            <Link
              className={`${
                location.pathname === "/dashboard/app/system-list" ||
                location.pathname === "/dashboard/app/system-add" ||
                location.pathname === "/dashboard/app/system-edit"
                  ? "active"
                  : ""
              } nav-link `}
              aria-current="page"
              to={`/dashboard/app/${
                isSuperAdmin() ||
                (permissions?.system &&
                  Object.entries(permissions.system).some(
                    ([key, value]) =>
                      ["status", "delete", "view", "update"].includes(key) &&
                      value
                  ))
                  ? "system-list"
                  : "system-add"
              }`}
              onClick={() => {}}
            >
              <i className="icon">
                <GrSystem style={{ width: "20px" }} />
              </i>
              <span className="item-name">Systems</span>
            </Link>
          </li>
        )}

        {(isSuperAdmin() ||
          ((isAdmin() || isWarehouse()) && rackManagementHasPermission)) && (
          <li
            className={`${
              location.pathname === "/dashboard/app/rack-list" ||
              location.pathname === "/dashboard/app/rack-add" ||
              location.pathname === "/dashboard/app/rack-edit"
                ? "active"
                : ""
            } nav-item `}
          >
            <Link
              className={`${
                location.pathname === "/dashboard/app/rack-list" ||
                location.pathname === "/dashboard/app/rack-add" ||
                location.pathname === "/dashboard/app/rack-edit"
                  ? "active"
                  : ""
              } nav-link `}
              aria-current="page"
              to={`/dashboard/app/${
                isSuperAdmin() ||
                (permissions?.rack_management &&
                  Object.entries(permissions.rack_management).some(
                    ([key, value]) =>
                      [
                        "status",
                        "delete",
                        "view",
                        "update",
                        "update_bin_status",
                      ].includes(key) && value
                  ))
                  ? "rack-list"
                  : "rack-add"
              }`}
              onClick={() => {}}
            >
              <i className="icon">
                <svg
                  id="Layer_1"
                  width="20"
                  data-name="Layer 1"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 118.79 122.88"
                >
                  <path
                    fill="currentColor"
                    d="M92.42,0a15.48,15.48,0,0,1,11,26.42h0a15.53,15.53,0,0,1-15.13,4l-.9,1.7a34.93,34.93,0,0,1,4.82,4,34.2,34.2,0,0,1,0,48.35l-.74.71,6,7.92a15.18,15.18,0,0,1,5.85-1.15,15.44,15.44,0,0,1,10.94,4.53h0a15.48,15.48,0,1,1-21.88,21.9h0a15.5,15.5,0,0,1-.16-21.74l-5.76-7.56A34.17,34.17,0,0,1,39.21,78.72L29,84a15.47,15.47,0,0,1-2.59,18.44h0a15.48,15.48,0,0,1-21.88,0h0a15.5,15.5,0,0,1,0-21.9l.18-.16a15.46,15.46,0,0,1,20-1.3A1.75,1.75,0,0,1,25,79l11.29-5.87A34.24,34.24,0,0,1,39,42.1L26.9,32.78l-.06,0a15.45,15.45,0,0,1-21.7-.16h0A15.48,15.48,0,1,1,27,10.78h0a15.5,15.5,0,0,1,3.41,16.77,2.82,2.82,0,0,1,.31.21l12.12,9.33,1-1a34.23,34.23,0,0,1,38-7.1l.81-1.53c-.4-.32-.78-.67-1.15-1l-.16-.18a15.45,15.45,0,0,1,.16-21.7h0A15.45,15.45,0,0,1,92.42,0ZM69.24,61.46H66.73a.18.18,0,0,0-.1,0,.13.13,0,0,0,0,.1v5.28a.18.18,0,0,0,0,.1.13.13,0,0,0,.1,0h2.5a.15.15,0,0,0,.1,0,.16.16,0,0,0,0-.1V61.6a.14.14,0,0,0,0-.1.11.11,0,0,0-.09,0ZM53,50.26h8.67V47.77A1.37,1.37,0,0,1,63,46.41H73a1.37,1.37,0,0,1,1.36,1.36v2.49H83a1.93,1.93,0,0,1,1.92,1.93v4.64a42.85,42.85,0,0,1-6.73,3.8A37.35,37.35,0,0,1,71,63V61.18a1.54,1.54,0,0,0-1.55-1.55h-2.9A1.54,1.54,0,0,0,65,61.18V63a37.4,37.4,0,0,1-7-2.36,43.76,43.76,0,0,1-6.91-3.92V52.19A1.95,1.95,0,0,1,53,50.26Zm31.93,9.33V72.2a1.87,1.87,0,0,1-.56,1.35,1.92,1.92,0,0,1-1.36.57H53a1.95,1.95,0,0,1-1.93-1.92V59.47a44.57,44.57,0,0,0,5.87,3.18A39.32,39.32,0,0,0,65,65.3v2a1.57,1.57,0,0,0,.45,1.1,1.54,1.54,0,0,0,1.09.45h2.91A1.54,1.54,0,0,0,71,67.3V65.21l0,.12a39.92,39.92,0,0,0,8.22-2.68,43.93,43.93,0,0,0,5.68-3.06Zm-13-11.32H64.06a.13.13,0,0,0-.12,0,.17.17,0,0,0-.06.12v1.77h8.2V48.44a.17.17,0,0,0-.17-.17ZM82.77,35.61a3,3,0,0,1-.74-.28,3.45,3.45,0,0,1-.75-.55,28.21,28.21,0,1,0,1.49.83Zm27.33,65a9.62,9.62,0,0,0-13.44-.13l-.13.14a9.58,9.58,0,0,0,0,13.56h0a9.59,9.59,0,1,0,13.56-13.56ZM22.27,84.74a9.62,9.62,0,0,0-13.44-.13l-.13.14a9.58,9.58,0,0,0,0,13.56h0a9.58,9.58,0,0,0,13.56,0h0a9.62,9.62,0,0,0,0-13.57Zm.59-69.81A9.59,9.59,0,0,0,9.3,28.5h0a9.62,9.62,0,0,0,13.44.13l.13-.14a9.61,9.61,0,0,0,0-13.56ZM99.2,8.7a9.58,9.58,0,0,0-13.56,0h0a9.61,9.61,0,0,0-.12,13.44l.13.12a9.58,9.58,0,0,0,13.56,0h0a9.61,9.61,0,0,0,0-13.57Z"
                  />
                </svg>
              </i>
              <span className="item-name">Racks</span>
            </Link>
          </li>
        )}

        {isSuperAdmin() && (
          <li
            className={`${
              location.pathname === "/dashboard/app/permission-list" ||
              location.pathname === "/dashboard/app/permission-add" ||
              location.pathname === "/dashboard/app/permission-edit"
                ? "active"
                : ""
            } nav-item `}
          >
            <Link
              className={`${
                location.pathname === "/dashboard/app/permission-list" ||
                location.pathname === "/dashboard/app/permission-add" ||
                location.pathname === "/dashboard/app/permission-edit"
                  ? "active"
                  : ""
              } nav-link `}
              aria-current="page"
              to="/dashboard/app/permission-list"
              onClick={() => {}}
            >
              <i className="icon">
                <AttractionsIcon style={{ width: "20px" }} />
              </i>
              <span className="item-name">Permissions</span>
            </Link>
          </li>
        )}

        {(isSuperAdmin() ||
          ((isAdmin() || isWarehouse()) && jobHasPermission)) && (
          <li
            className={`${
              location.pathname === "/dashboard/app/job-list" ||
              location.pathname === "/dashboard/app/job-add" ||
              location.pathname === "/dashboard/app/job-edit"
                ? "active"
                : ""
            } nav-item `}
          >
            <Link
              className={`${
                location.pathname === "/dashboard/app/job-list" ||
                location.pathname === "/dashboard/app/job-add" ||
                location.pathname === "/dashboard/app/job-edit"
                  ? "active"
                  : ""
              } nav-link `}
              aria-current="page"
              to={`/dashboard/app/${
                isSuperAdmin() ||
                (permissions?.job &&
                  Object.entries(permissions.job).some(
                    ([key, value]) =>
                      [
                        "status",
                        "download_reports",
                        "delete",
                        "view",
                        "update",
                        "view_job_product_report",
                        "update_job_item_status",
                        "create_bom",
                        "view_bom",
                        "delete_bom",
                        "edit_bom",
                        "create_bom_pr",
                        "print_bom",
                      ].includes(key) && value
                  ))
                  ? "job-list"
                  : "job-add"
              }`}
              onClick={() => {}}
            >
              <i className="icon">
                <WorkHistoryIcon style={{ width: "20px" }} />
              </i>
              <span className="item-name">Jobs</span>
            </Link>
          </li>
        )}

        {(isSuperAdmin() || productRequestHasPermission) && (
          <li
            className={`${
              location.pathname === "/dashboard/app/product-request-list" ||
              location.pathname === "/dashboard/app/product-request-add" ||
              location.pathname === "/dashboard/app/product-request-edit"
                ? "active"
                : ""
            } nav-item `}
          >
            <Link
              className={`${
                location.pathname === "/dashboard/app/product-request-list" ||
                location.pathname === "/dashboard/app/product-request-add" ||
                location.pathname === "/dashboard/app/product-request-edit"
                  ? "active"
                  : ""
              } nav-link `}
              aria-current="page"
              to={`/dashboard/app/${
                isSuperAdmin() ||
                (permissions?.product_request &&
                  Object.entries(permissions.product_request).some(
                    ([key, value]) =>
                      ["delete", "view", "update"].includes(key) && value
                  ))
                  ? "product-request-list"
                  : (isSuperAdmin() ||
                      (permissions?.product_request &&
                        Object.entries(permissions.product_request).some(
                          ([key, value]) =>
                            ["add_custom_product", "create"].includes(key) &&
                            value
                        ))) &&
                    "product-request-add"
              }`}
              onClick={() => {}}
            >
              <i className="icon">
                <InventoryIcon style={{ width: "20px" }} />
              </i>
              <span className="item-name">Product Requests</span>
            </Link>
          </li>
        )}

        {(isSuperAdmin() ||
          ((isPurchasing() || isAdmin() || isSupplier()) &&
            orderHasPermission)) && (
          <li
            className={`${
              location.pathname === "/dashboard/app/purchase-order-list" ||
              location.pathname === "/dashboard/app/purchase-order-add" ||
              location.pathname === "/dashboard/app/purchase-order-edit"
                ? "active"
                : ""
            } nav-item `}
          >
            <Link
              className={`${
                location.pathname === "/dashboard/app/purchase-order-list" ||
                location.pathname === "/dashboard/app/purchase-order-add" ||
                location.pathname === "/dashboard/app/purchase-order-edit"
                  ? "active"
                  : ""
              } nav-link `}
              aria-current="page"
              to={`/dashboard/app/${
                isSuperAdmin() ||
                ((isPurchasing() || isAdmin() || isSupplier()) &&
                  permissions?.purchase_order &&
                  Object.entries(permissions?.purchase_order).some(
                    ([key, value]) =>
                      ["status", "delete", "view", "update"].includes(key) &&
                      value
                  ))
                  ? "purchase-order-list"
                  : "purchase-order-add"
              }`}
              onClick={() => {}}
            >
              <i className="icon">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect
                    x="4"
                    y="2"
                    width="16"
                    height="20"
                    rx="2"
                    stroke="currentColor"
                    strokeWidth="2"
                    fill="none"
                  />

                  <path
                    d="M8 7h8M8 12h8M8 17h6"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />

                  <path
                    d="M6 7l1 1 2-2M6 12l1 1 2-2"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />

                  <circle
                    cx="6.5"
                    cy="17"
                    r="1"
                    stroke="currentColor"
                    strokeWidth="2"
                    fill="none"
                  />
                </svg>
              </i>
              <span className="item-name">PO / Receiving</span>
            </Link>
          </li>
        )}

        {(isSuperAdmin() ||
          ((isAdmin() || isSupplier() || isPurchasing() || isWarehouse()) &&
            orderReceiveHasPermission)) && (
          <li
            className={`${
              location.pathname === "/dashboard/app/receiving-order"
                ? "active"
                : ""
            } nav-item `}
          >
            <Link
              className={`${
                location.pathname === "/dashboard/app/receiving-order"
                  ? "active"
                  : ""
              } nav-link `}
              aria-current="page"
              to={`/dashboard/app/${
                (isSuperAdmin() ||
                  (permissions?.receiving &&
                    Object.entries(permissions.receiving).some(
                      ([key, value]) => ["view"].includes(key) && value
                    ))) &&
                "receiving-order"
              }`}
              onClick={() => {}}
            >
              <i className="icon">
                <ShoppingBasketIcon style={{ width: "20px" }} />
              </i>
              <span className="item-name">Receiving Log</span>
            </Link>
          </li>
        )}
      </Accordion>
    </Fragment>
  );
});

export default VerticalNav;
